import { cn } from "@shared/lib/utils";
import { Button } from "@shared/ui/button";
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";
import React, { useState } from "react";

type AlertVariant = "info" | "warning" | "error" | "success";

interface AlertBannerProps {
  variant?: AlertVariant;
  title?: string;
  message: string;
  dismissible?: boolean;
  className?: string;
  icon?: React.ReactNode;
}

const variantConfig = {
  info: {
    container: "bg-blue-50 border-blue-200 text-blue-900",
    icon: <Info className="h-5 w-5 text-blue-600" />,
    titleColor: "text-blue-900",
  },
  warning: {
    container: "bg-yellow-50 border-yellow-200 text-yellow-900",
    icon: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
    titleColor: "text-yellow-900",
  },
  error: {
    container: "bg-red-50 border-red-200 text-red-900",
    icon: <AlertCircle className="h-5 w-5 text-red-600" />,
    titleColor: "text-red-900",
  },
  success: {
    container: "bg-green-50 border-green-200 text-green-900",
    icon: <CheckCircle className="h-5 w-5 text-green-600" />,
    titleColor: "text-green-900",
  },
};

export const AlertBanner: React.FC<AlertBannerProps> = ({
  variant = "info",
  title,
  message,
  dismissible = false,
  className,
  icon,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const config = variantConfig[variant];

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg border",
        config.container,
        className
      )}
    >
      <div className="flex-shrink-0 mt-0.5">{icon || config.icon}</div>
      <div className="flex-1 min-w-0">
        {title && (
          <h3 className={cn("font-semibold mb-1", config.titleColor)}>
            {title}
          </h3>
        )}
        <p className="text-sm leading-relaxed">{message}</p>
      </div>
      {dismissible && (
        <Button
          onClick={() => setIsVisible(false)}
          className="flex-shrink-0 p-1 hover:bg-black/5 rounded transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};
